// Next-update SLA for blood-draw submissions.
//
// Single source of truth shared by:
//   - Blood Files list (SubmissionsList) — "Next Update" column + overdue filter
//   - Daily Tasks dashboard (DailyTasks) — the "call patients" queue
//
// An active draw being chased by admin should get a status update every
// SLA_HOURS, measured from the last status update (outcome_logged_at). The SLA
// only fires when admin actually has a follow-up to do — outreach, rescheduling,
// missing info, etc. Once a patient is scheduled / confirmed, the phleb is on
// their way, or the draw is done, the clock pauses.

export const SLA_HOURS = 3;

// Outcome codes that mean "no follow-up needed right now" → pause the SLA.
// Anything NOT in this set falls back to the SLA cadence (voicemail_left,
// no_answer, text_sent, email_sent, reached_patient, missing_info,
// reschedule_requested, no_show, address_wrong, etc.).
export const SLA_EXEMPT_OUTCOMES = new Set([
  // Booked — wait for the appointment.
  'scheduled',
  'appointment_confirmed',
  // Phleb is actively working the visit — no admin action needed.
  'en_route',
  'arrived',
  // Sample collected — admin doesn't need to chase this anymore.
  'successful_draw',
  'partial_draw',
  'difficult_stick_completed',
  'spun',
  'dropped_off_lab',
  'shipped',
  // Terminal cancellations.
  'patient_refused',
  'cancelled_by_patient',
]);

// Returns one of:
//   { state: 'done' }              — completed/cancelled/exempt; clock paused
//   { state: 'none' }              — actionable but never touched (needs first contact)
//   { state: 'due', dueMs }        — actionable, within the SLA window
//   { state: 'overdue', dueMs }    — actionable, past the SLA window (dueMs <= 0)
export const getNextUpdateInfo = (submission) => {
  const status = (submission?.status || '').toLowerCase();
  if (status === 'completed' || status === 'cancelled' || status === 'out_of_area') return { state: 'done' };
  const outcome = (submission?.outcome_code || '').toLowerCase();
  if (SLA_EXEMPT_OUTCOMES.has(outcome)) return { state: 'done' };
  const ts = submission?.outcome_logged_at ? new Date(submission.outcome_logged_at) : null;
  if (!ts || isNaN(ts)) return { state: 'none' };
  const dueMs = ts.getTime() + SLA_HOURS * 3600000 - Date.now();
  return { state: dueMs <= 0 ? 'overdue' : 'due', dueMs };
};

export const fmtDur = (ms) => {
  const m = Math.round(Math.abs(ms) / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

// A draw is "done" (no longer actionable for the phleb) once it's drawn, in the
// lab, completed, or cancelled.
export const DONE_DRAW_STATUSES = new Set([
  'completed', 'cancelled', 'waiting_on_lab_results', 'waiting_to_be_received',
]);

export const isOpenDraw = (s) => {
  const st = (s?.status || '').toLowerCase();
  if (DONE_DRAW_STATUSES.has(st)) return false;
  if ((s?.outcome_code || '') === 'successful_draw') return false; // drawn
  return true;
};
